import { useMemo, useState } from "react";
import { useStore } from "../store/store";
import type { Agent, Conversation } from "../store/types";

const SLA_WARN = 5;
const SLA_BREACH = 10;

function minutesSince(iso?: string): number | null {
  if (!iso) return null;
  return Math.round((Date.now() - new Date(iso).getTime()) / 60000);
}

function maskPhone(p: string): string {
  return `••• ••• ${p.slice(-4)}`;
}

export function Supervisor() {
  const conversations = useStore((s) => s.conversations);
  const contacts = useStore((s) => s.contacts);
  const events = useStore((s) => s.events);
  const agents = useStore((s) => s.agents);
  const updateConversation = useStore((s) => s.updateConversation);

  const [whisperTarget, setWhisperTarget] = useState<Agent | null>(null);
  const [whisperText, setWhisperText] = useState("");

  // Floor-wide KPIs
  const stats = useMemo(() => {
    const awaiting = conversations.filter((c) => c.state === "awaiting_agent");
    const breaches = awaiting.filter((c) => (minutesSince(c.lastInboundAt) ?? 0) >= SLA_BREACH).length;
    const warns = awaiting.filter((c) => {
      const m = minutesSince(c.lastInboundAt) ?? 0;
      return m >= SLA_WARN && m < SLA_BREACH;
    }).length;
    const live = conversations.filter((c) => c.state === "awaiting_voter").length;
    const escalated = conversations.filter((c) => c.state === "escalated").length;
    const completed = conversations.filter((c) => c.state === "completed").length;
    const optedOut = conversations.filter((c) => c.state === "opted_out").length;
    const handledToday = agents.reduce((acc, a) => acc + a.handledToday, 0);
    const optOutsToday = agents.reduce((acc, a) => acc + a.optOutsToday, 0);
    const optOutRate = handledToday > 0 ? (optOutsToday / handledToday) * 100 : 0;
    return { awaiting: awaiting.length, breaches, warns, live, escalated, completed, optedOut, handledToday, optOutsToday, optOutRate };
  }, [conversations, agents]);

  const escalations = useMemo(
    () => conversations.filter((c) => c.state === "escalated"),
    [conversations],
  );

  const queue = useMemo(
    () =>
      [...conversations]
        .filter((c) => c.state === "awaiting_agent")
        .sort((a, b) => new Date(a.lastInboundAt ?? a.startedAt).getTime() - new Date(b.lastInboundAt ?? b.startedAt).getTime()),
    [conversations],
  );

  function takeOver(convo: Conversation) {
    updateConversation(convo.id, { assignedAgentId: "agent_me", state: "awaiting_agent", flagged: false });
  }

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Supervisor floor</h1>
          <p className="text-sm text-slate-500">Live monitor of the messaging floor. Whisper to agents, take over conversations, watch SLA pressure.</p>
        </div>
        <div className="flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-500" />
          </span>
          Live · updated just now
        </div>
      </header>

      {/* KPI strip */}
      <div className="mb-6 grid grid-cols-6 gap-3">
        <Kpi label="Awaiting agent" value={stats.awaiting} tone="brand" />
        <Kpi label="SLA breach (>10m)" value={stats.breaches} tone={stats.breaches > 0 ? "danger" : "ink"} />
        <Kpi label="In conversation" value={stats.live} tone="ink" />
        <Kpi label="Escalated" value={stats.escalated} tone={stats.escalated > 0 ? "danger" : "ink"} />
        <Kpi label="Handled today" value={stats.handledToday} tone="ink" />
        <Kpi label="Opt-out rate" value={`${stats.optOutRate.toFixed(1)}%`} tone="accent" />
      </div>

      <div className="grid grid-cols-[1fr_320px] gap-6">
        {/* LEFT — agents + queue */}
        <div className="space-y-6">
          {/* Agent roster */}
          <section className="card card-pad">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-semibold">Agent roster ({agents.filter((a) => a.state !== "off").length} on shift)</h2>
              <div className="flex items-center gap-3 text-[11px] text-slate-500">
                <Legend color="bg-brand-500" label="Available" />
                <Legend color="bg-accent-400" label="Wrap-up" />
                <Legend color="bg-slate-700" label="Break" />
                <Legend color="bg-slate-300" label="Off" />
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Agent</th>
                    <th className="px-3 py-2 text-left">State</th>
                    <th className="px-3 py-2 text-left">Lang</th>
                    <th className="px-3 py-2 text-right">Handled</th>
                    <th className="px-3 py-2 text-right">Opt-out</th>
                    <th className="px-3 py-2 text-right">Avg reply</th>
                    <th className="px-3 py-2 text-right">Escal.</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {agents.map((a) => {
                    const optRate = a.handledToday > 0 ? (a.optOutsToday / a.handledToday) * 100 : 0;
                    const hot = optRate > 8;
                    return (
                      <tr key={a.id} className="bg-white">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="grid h-7 w-7 place-items-center rounded-full bg-ink-900 text-[10px] font-bold text-white">
                              {a.initials}
                            </span>
                            <span className="font-medium text-ink-900">{a.name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <StateBadge state={a.state} />
                        </td>
                        <td className="px-3 py-2 text-xs uppercase text-slate-600">{a.languages.join(" · ")}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{a.handledToday}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${hot ? "font-semibold text-red-600" : ""}`}>
                          {a.optOutsToday}{hot ? " ⚠" : ""}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{a.avgResponseSec}s</td>
                        <td className="px-3 py-2 text-right tabular-nums">{a.escalations}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setWhisperTarget(a)}
                            disabled={a.state === "off"}
                            className="rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                          >
                            Whisper
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Live queue */}
          <section className="card card-pad">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-semibold">Unassigned queue ({queue.length})</h2>
              <p className="text-[11px] text-slate-500">Sorted oldest-inbound first · SLA target: under 5 min</p>
            </div>
            <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
              {queue.map((c) => {
                const ct = contacts.find((x) => x.id === c.contactId);
                const last = events
                  .filter((e) => e.contactId === c.contactId && e.campaignId === c.campaignId)
                  .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];
                const mins = minutesSince(c.lastInboundAt) ?? 0;
                const tone = mins >= SLA_BREACH ? "breach" : mins >= SLA_WARN ? "warn" : "ok";
                return (
                  <li key={c.id} className="flex items-center gap-3 bg-white px-3 py-2.5">
                    <span
                      className={`h-2.5 w-2.5 flex-none rounded-full ${
                        tone === "breach" ? "bg-red-500 animate-pulse" : tone === "warn" ? "bg-accent-400" : "bg-brand-500"
                      }`}
                    />
                    <span className="w-28 flex-none text-sm font-semibold text-ink-900">
                      {ct?.firstName}
                      <span className="ml-1 rounded bg-slate-200 px-1 text-[9px] font-bold uppercase text-slate-600">
                        {c.language}
                      </span>
                    </span>
                    <span className="w-28 flex-none font-mono text-[11px] text-slate-500">
                      {ct ? maskPhone(ct.phoneE164) : "—"}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs text-slate-600">"{last?.body ?? "—"}"</span>
                    <span
                      className={`flex-none text-xs font-semibold tabular-nums ${
                        tone === "breach" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "text-slate-500"
                      }`}
                    >
                      {mins}m
                    </span>
                    <button
                      onClick={() => takeOver(c)}
                      className="flex-none rounded bg-ink-900 px-2 py-1 text-[11px] font-semibold text-white hover:bg-ink-700"
                    >
                      Take over
                    </button>
                  </li>
                );
              })}
              {queue.length === 0 && (
                <li className="px-3 py-6 text-center text-xs text-slate-400">No unassigned conversations.</li>
              )}
            </ul>
          </section>
        </div>

        {/* RIGHT — escalations */}
        <aside className="card card-pad">
          <h2 className="mb-3 flex items-center gap-2 font-semibold">
            <span className="grid h-5 w-5 place-items-center rounded bg-red-100 text-[12px] font-bold text-red-700">!</span>
            Escalations ({escalations.length})
          </h2>
          <ul className="space-y-3">
            {escalations.map((c) => {
              const ct = contacts.find((x) => x.id === c.contactId);
              const owner = agents.find((a) => a.id === c.assignedAgentId);
              return (
                <li key={c.id} className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-ink-900">{ct?.firstName}</div>
                    <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-700">
                      {c.language}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-600">From: {owner?.name ?? "Unassigned"}</div>
                  <div className="mt-1 text-xs text-red-800">{c.escalationReason ?? "—"}</div>
                  <div className="mt-2 flex gap-1">
                    <button
                      onClick={() => takeOver(c)}
                      className="flex-1 rounded bg-ink-900 px-2 py-1 text-[11px] font-semibold text-white hover:bg-ink-700"
                    >
                      Take over
                    </button>
                    <button
                      onClick={() =>
                        updateConversation(c.id, { state: "awaiting_voter", flagged: false, escalationReason: undefined })
                      }
                      className="flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Resolve
                    </button>
                  </div>
                </li>
              );
            })}
            {escalations.length === 0 && (
              <li className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center text-xs text-slate-400">
                Nothing escalated.
              </li>
            )}
          </ul>
        </aside>
      </div>

      {/* Whisper modal */}
      {whisperTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/50 p-4" onClick={() => setWhisperTarget(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 text-[11px] uppercase tracking-wider text-slate-500">Whisper to agent</div>
            <div className="mb-3 text-lg font-semibold">{whisperTarget.name}</div>
            <p className="mb-3 text-xs text-slate-500">
              The agent will see this in their console privately. The voter never sees whisper messages.
            </p>
            <textarea
              value={whisperText}
              onChange={(e) => setWhisperText(e.target.value)}
              placeholder="e.g. Try offering polling-location info before the issue question…"
              className="input h-24 resize-none"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={() => {
                  setWhisperTarget(null);
                  setWhisperText("");
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // In a real system we'd push to the agent's session over WS; demo-only.
                  setWhisperTarget(null);
                  setWhisperText("");
                }}
                disabled={!whisperText.trim()}
                className="btn-primary disabled:opacity-50"
              >
                Send whisper
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number | string; tone: "brand" | "accent" | "ink" | "danger" }) {
  const stripe =
    tone === "brand"
      ? "before:bg-brand-500"
      : tone === "accent"
      ? "before:bg-accent-400"
      : tone === "danger"
      ? "before:bg-red-500"
      : "before:bg-ink-900";
  return (
    <div
      className={`card relative overflow-hidden p-4 transition hover:-translate-y-0.5 hover:shadow-md before:absolute before:left-0 before:top-0 before:h-full before:w-1 ${stripe}`}
    >
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-ink-900">{value}</div>
    </div>
  );
}

function StateBadge({ state }: { state: Agent["state"] }) {
  const map = {
    available: { dot: "bg-brand-500", text: "Available", cls: "text-brand-800" },
    wrap_up: { dot: "bg-accent-400", text: "Wrap-up", cls: "text-amber-800" },
    break: { dot: "bg-slate-700", text: "Break", cls: "text-slate-700" },
    off: { dot: "bg-slate-300", text: "Off shift", cls: "text-slate-500" },
  } as const;
  const m = map[state];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${m.cls}`}>
      <span className={`inline-block h-2 w-2 rounded-full ${m.dot}`} />
      {m.text}
    </span>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`h-2 w-2 rounded-full ${color}`} />
      {label}
    </span>
  );
}
